import { motion, useInView, Variants } from "framer-motion";
import { useRef, useMemo } from "react";

interface WordFadeInProps {
  text: string;
  className?: string;
  as?: "h1" | "h2" | "h3" | "h4" | "p" | "span";
  delay?: number;
  staggerDelay?: number;
  /** Highlight words wrapped in *asterisks* in primary color */
  highlightPrimary?: boolean;
}

const containerVariants: Variants = {
  hidden: {},
  visible: (staggerDelay: number) => ({
    transition: {
      staggerChildren: staggerDelay,
    },
  }),
};

const wordVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 20,
    filter: "blur(4px)",
  },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const WordFadeIn = ({
  text,
  className = "",
  as: Tag = "p",
  delay = 0,
  staggerDelay = 0.08,
  highlightPrimary = false,
}: WordFadeInProps) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });

  const tokens = useMemo(() => {
    if (!highlightPrimary) {
      return text.split("\n").map((line) => line.split(" ").map((w) => ({ word: w, highlight: false })));
    }

    return text.split("\n").map((line) =>
      line.split(" ").map((w) => {
        if (w.startsWith("*") && w.endsWith("*")) {
          return { word: w.slice(1, -1), highlight: true };
        }
        return { word: w, highlight: false };
      })
    );
  }, [text, highlightPrimary]);

  return (
    <motion.div
      ref={ref}
      variants={containerVariants}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      custom={staggerDelay}
      className={className}
      style={{ transitionDelay: `${delay}s` }}
    >
      <Tag className={className} style={{ textWrap: "pretty" as any }}>
        {tokens.map((line, lineIndex) => (
          <span key={lineIndex} className="block">
            {line.map((w, i) => (
              <motion.span
                key={`${w.word}-${i}`}
                variants={wordVariants}
                className={`inline-block mr-[0.3em] ${w.highlight ? "text-primary-bright" : ""}`}
              >
                {w.word}
              </motion.span>
            ))}
          </span>
        ))}
      </Tag>
    </motion.div>
  );
};

export default WordFadeIn;